import Dexie, { type EntityTable } from 'dexie';

// =============================================================================
// TYPES
// =============================================================================

export type SyncStatus = 'pending' | 'uploading' | 'synced' | 'failed';
export type OperationType = 'create' | 'update' | 'delete';

export interface PendingServiceLog {
  id: string;  // Local UUID
  timestamp: string;  // ISO datetime
  employeeId: string;
  employeeName: string;
  clientId: string;
  clientName: string;
  timeLog: string;
  visitLog: string;
  missedService: string | null;
  serviceNotes: string | null;
  latitude: number;
  longitude: number;
  imageBlob?: Blob;  // Store image locally until uploaded
  imageUrl?: string;  // Firebase URL after upload
  syncStatus: SyncStatus;
  retryCount: number;
  createdAt: string;
  lastAttempt?: string;
  errorMessage?: string;
}

export interface PendingWorkOrder {
  id: string;  // Local UUID
  operation: OperationType;
  entityId?: number;  // Server ID (for update/delete)
  data: Record<string, unknown>;  // Payload (work order fields)
  syncStatus: SyncStatus;
  retryCount: number;
  createdAt: string;
  lastAttempt?: string;
  errorMessage?: string;
}

export interface PendingClient {
  id: string;  // Local UUID
  operation: OperationType;
  entityId?: number;  // Server ID (for update/delete)
  data: Record<string, unknown>;  // Payload (client fields)
  syncStatus: SyncStatus;
  retryCount: number;
  createdAt: string;
  lastAttempt?: string;
  errorMessage?: string;
}

export interface PendingSchedule {
  id: string;  // Local UUID
  operation: OperationType;
  entityId?: string;  // Composite key: "date-employeeId-routeCode"
  data: Record<string, unknown>;  // Payload (schedule fields)
  syncStatus: SyncStatus;
  retryCount: number;
  createdAt: string;
  lastAttempt?: string;
  errorMessage?: string;
}

// =============================================================================
// DATABASE
// =============================================================================

class OfflineQueueDB extends Dexie {
  serviceLogs!: EntityTable<PendingServiceLog, 'id'>;
  workOrders!: EntityTable<PendingWorkOrder, 'id'>;
  clients!: EntityTable<PendingClient, 'id'>;
  schedules!: EntityTable<PendingSchedule, 'id'>;

  constructor() {
    super('TWGOfflineQueue');

    // Version 2: Add work orders, clients, schedules tables
    this.version(2).stores({
      serviceLogs: 'id, syncStatus, createdAt, clientId, employeeId',
      workOrders: 'id, syncStatus, createdAt, operation, entityId',
      clients: 'id, syncStatus, createdAt, operation, entityId',
      schedules: 'id, syncStatus, createdAt, operation, entityId',
    }).upgrade((tx) => {
      // Migration from v1 to v2 - just create new tables
      console.log('Upgrading offline queue to v2 - adding work orders, clients, schedules');
      return tx.table('serviceLogs').toArray();  // Verify existing data
    });

    // Version 1: Original schema (service logs only)
    this.version(1).stores({
      serviceLogs: 'id, syncStatus, createdAt, clientId, employeeId',
    });
  }
}

export const db = new OfflineQueueDB();

// =============================================================================
// HELPERS
// =============================================================================

function generateVisitLog(
  employeeName: string,
  timestamp: Date,
  clientId: string
): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const firstName = employeeName.split(' ')[0];
  const day = days[timestamp.getDay()];
  const month = timestamp.getMonth() + 1;
  const date = timestamp.getDate();
  const hours = timestamp.getHours();
  const minutes = timestamp.getMinutes().toString().padStart(2, '0');
  const seconds = timestamp.getSeconds().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;

  return `${firstName} ${day} ${month}/${date} ${hour12}:${minutes}:${seconds} ${ampm} ${clientId}`;
}

function formatTimeLog(timestamp: Date): string {
  const hours = timestamp.getHours();
  const minutes = timestamp.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// =============================================================================
// QUEUE OPERATIONS
// =============================================================================

export interface CreateServiceLogInput {
  employeeId: string;
  employeeName: string;
  clientId: string;
  clientName: string;
  latitude: number;
  longitude: number;
  missedService?: string | null;
  serviceNotes?: string | null;
  imageBlob?: Blob;
}

/**
 * Add a service log to the offline queue
 * Returns the local ID for tracking
 */
export async function queueServiceLog(input: CreateServiceLogInput): Promise<string> {
  const now = new Date();
  const id = generateUUID();

  const log: PendingServiceLog = {
    id,
    timestamp: now.toISOString(),
    employeeId: input.employeeId,
    employeeName: input.employeeName,
    clientId: input.clientId,
    clientName: input.clientName,
    timeLog: formatTimeLog(now),
    visitLog: generateVisitLog(input.employeeName, now, input.clientId),
    missedService: input.missedService || null,
    serviceNotes: input.serviceNotes || null,
    latitude: input.latitude,
    longitude: input.longitude,
    imageBlob: input.imageBlob,
    imageUrl: undefined,
    syncStatus: 'pending',
    retryCount: 0,
    createdAt: now.toISOString(),
  };

  await db.serviceLogs.add(log);
  return id;
}

/**
 * Get all pending service logs
 */
export async function getPendingLogs(): Promise<PendingServiceLog[]> {
  return db.serviceLogs
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .sortBy('createdAt');
}

/**
 * Get count of pending logs
 */
export async function getPendingCount(): Promise<number> {
  return db.serviceLogs
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .count();
}

/**
 * Update sync status of a log
 */
export async function updateLogStatus(
  id: string,
  status: SyncStatus,
  errorMessage?: string
): Promise<void> {
  const updates: Partial<PendingServiceLog> = {
    syncStatus: status,
    lastAttempt: new Date().toISOString(),
  };

  if (status === 'failed') {
    const log = await db.serviceLogs.get(id);
    if (log) {
      updates.retryCount = (log.retryCount || 0) + 1;
      updates.errorMessage = errorMessage;
    }
  }

  if (status === 'synced') {
    updates.errorMessage = undefined;
  }

  await db.serviceLogs.update(id, updates);
}

/**
 * Set image URL after upload
 */
export async function setLogImageUrl(id: string, imageUrl: string): Promise<void> {
  await db.serviceLogs.update(id, { imageUrl });
}

/**
 * Remove synced logs (cleanup)
 */
export async function cleanupSyncedLogs(olderThanDays: number = 7): Promise<number> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - olderThanDays);

  const toDelete = await db.serviceLogs
    .where('syncStatus')
    .equals('synced')
    .filter(log => new Date(log.createdAt) < cutoff)
    .toArray();

  await db.serviceLogs.bulkDelete(toDelete.map(l => l.id));
  return toDelete.length;
}

/**
 * Get a specific log by ID
 */
export async function getLog(id: string): Promise<PendingServiceLog | undefined> {
  return db.serviceLogs.get(id);
}

/**
 * Delete a log (for manual removal of failed logs)
 */
export async function deleteLog(id: string): Promise<void> {
  await db.serviceLogs.delete(id);
}

/**
 * Get all logs for today by employee
 */
export async function getTodaysLogs(employeeId: string): Promise<PendingServiceLog[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return db.serviceLogs
    .where('employeeId')
    .equals(employeeId)
    .filter(log => new Date(log.createdAt) >= today)
    .sortBy('createdAt');
}

/**
 * Check if a client has already been logged today
 */
export async function hasLoggedToday(
  employeeId: string,
  clientId: string
): Promise<boolean> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const count = await db.serviceLogs
    .where('employeeId')
    .equals(employeeId)
    .filter(log =>
      log.clientId === clientId &&
      new Date(log.createdAt) >= today
    )
    .count();

  return count > 0;
}

// =============================================================================
// WORK ORDERS QUEUE
// =============================================================================

/**
 * Queue a work order mutation (create, update, delete)
 */
export async function queueWorkOrder(
  operation: OperationType,
  data: Record<string, unknown>,
  entityId?: number
): Promise<string> {
  const id = generateUUID();

  const pending: PendingWorkOrder = {
    id,
    operation,
    entityId,
    data,
    syncStatus: 'pending',
    retryCount: 0,
    createdAt: new Date().toISOString(),
  };

  await db.workOrders.add(pending);
  return id;
}

/**
 * Get all pending work orders
 */
export async function getPendingWorkOrders(): Promise<PendingWorkOrder[]> {
  return db.workOrders
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .sortBy('createdAt');
}

/**
 * Update work order sync status
 */
export async function updateWorkOrderStatus(
  id: string,
  status: SyncStatus,
  errorMessage?: string
): Promise<void> {
  const updates: Partial<PendingWorkOrder> = {
    syncStatus: status,
    lastAttempt: new Date().toISOString(),
  };

  if (status === 'failed') {
    const wo = await db.workOrders.get(id);
    if (wo) {
      updates.retryCount = (wo.retryCount || 0) + 1;
      updates.errorMessage = errorMessage;
    }
  }

  if (status === 'synced') {
    updates.errorMessage = undefined;
  }

  await db.workOrders.update(id, updates);
}

/**
 * Cleanup synced work orders
 */
export async function cleanupSyncedWorkOrders(olderThanDays: number = 7): Promise<number> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - olderThanDays);

  const toDelete = await db.workOrders
    .where('syncStatus')
    .equals('synced')
    .filter(wo => new Date(wo.createdAt) < cutoff)
    .toArray();

  await db.workOrders.bulkDelete(toDelete.map(w => w.id));
  return toDelete.length;
}

// =============================================================================
// CLIENTS QUEUE
// =============================================================================

/**
 * Queue a client mutation (create, update, delete)
 */
export async function queueClient(
  operation: OperationType,
  data: Record<string, unknown>,
  entityId?: number
): Promise<string> {
  const id = generateUUID();

  const pending: PendingClient = {
    id,
    operation,
    entityId,
    data,
    syncStatus: 'pending',
    retryCount: 0,
    createdAt: new Date().toISOString(),
  };

  await db.clients.add(pending);
  return id;
}

/**
 * Get all pending clients
 */
export async function getPendingClients(): Promise<PendingClient[]> {
  return db.clients
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .sortBy('createdAt');
}

/**
 * Update client sync status
 */
export async function updateClientStatus(
  id: string,
  status: SyncStatus,
  errorMessage?: string
): Promise<void> {
  const updates: Partial<PendingClient> = {
    syncStatus: status,
    lastAttempt: new Date().toISOString(),
  };

  if (status === 'failed') {
    const client = await db.clients.get(id);
    if (client) {
      updates.retryCount = (client.retryCount || 0) + 1;
      updates.errorMessage = errorMessage;
    }
  }

  if (status === 'synced') {
    updates.errorMessage = undefined;
  }

  await db.clients.update(id, updates);
}

/**
 * Cleanup synced clients
 */
export async function cleanupSyncedClients(olderThanDays: number = 7): Promise<number> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - olderThanDays);

  const toDelete = await db.clients
    .where('syncStatus')
    .equals('synced')
    .filter(c => new Date(c.createdAt) < cutoff)
    .toArray();

  await db.clients.bulkDelete(toDelete.map(c => c.id));
  return toDelete.length;
}

// =============================================================================
// SCHEDULES QUEUE
// =============================================================================

/**
 * Queue a schedule mutation (create, update, delete)
 */
export async function queueSchedule(
  operation: OperationType,
  data: Record<string, unknown>,
  entityId?: string  // Composite key: "date-employeeId-routeCode"
): Promise<string> {
  const id = generateUUID();

  const pending: PendingSchedule = {
    id,
    operation,
    entityId,
    data,
    syncStatus: 'pending',
    retryCount: 0,
    createdAt: new Date().toISOString(),
  };

  await db.schedules.add(pending);
  return id;
}

/**
 * Get all pending schedules
 */
export async function getPendingSchedules(): Promise<PendingSchedule[]> {
  return db.schedules
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .sortBy('createdAt');
}

/**
 * Update schedule sync status
 */
export async function updateScheduleStatus(
  id: string,
  status: SyncStatus,
  errorMessage?: string
): Promise<void> {
  const updates: Partial<PendingSchedule> = {
    syncStatus: status,
    lastAttempt: new Date().toISOString(),
  };

  if (status === 'failed') {
    const schedule = await db.schedules.get(id);
    if (schedule) {
      updates.retryCount = (schedule.retryCount || 0) + 1;
      updates.errorMessage = errorMessage;
    }
  }

  if (status === 'synced') {
    updates.errorMessage = undefined;
  }

  await db.schedules.update(id, updates);
}

/**
 * Cleanup synced schedules
 */
export async function cleanupSyncedSchedules(olderThanDays: number = 7): Promise<number> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - olderThanDays);

  const toDelete = await db.schedules
    .where('syncStatus')
    .equals('synced')
    .filter(s => new Date(s.createdAt) < cutoff)
    .toArray();

  await db.schedules.bulkDelete(toDelete.map(s => s.id));
  return toDelete.length;
}

// =============================================================================
// GLOBAL QUEUE STATUS
// =============================================================================

/**
 * Get total count of all pending items across all queues
 */
export async function getTotalPendingCount(): Promise<number> {
  const [serviceLogs, workOrders, clients, schedules] = await Promise.all([
    db.serviceLogs.where('syncStatus').anyOf(['pending', 'failed']).count(),
    db.workOrders.where('syncStatus').anyOf(['pending', 'failed']).count(),
    db.clients.where('syncStatus').anyOf(['pending', 'failed']).count(),
    db.schedules.where('syncStatus').anyOf(['pending', 'failed']).count(),
  ]);

  return serviceLogs + workOrders + clients + schedules;
}
